import { useEffect, useMemo, useRef, useState } from "react";
import {
    FiCheck,
    FiChevronDown,
    FiMapPin,
    FiPlus,
    FiSearch,
    FiTrash2,
    FiUser,
} from "react-icons/fi";
import { MdOutlineBusiness } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import { getCurrentUserId } from "../../../utils";
import { getAddressDetails } from "../AddressBook/actions";
import { getPostedInvoiceByUserId } from "../Accounting/actions";
import { toast } from "react-toastify";

const sectionDefinitions = {
    Freight: [
        "Base Rate",
        "Additional Rate",
        "Lumpsum Charges",
        "Adcom (%)",
        "Others (%)",
        "Brokerage (%)",
    ],
    "Laytime Demurrage": [
        "Demurrage (Load Port)",
        "Demurrage (Disch Port)",
        "Demurrage Surcharge",
        "Adcom (%)",
        "Others (%)",
        "Brokerage (%)",
    ],
    "Laytime Despatch": [
        "Despatch (Load Port)",
        "Despatch (Disch Port)",
        "Despatch Adjustment",
        "Brokerage (%)",
    ],
    Adjustments: [
        "Fuel Settlement Adjustment",
        "Off-Hire Hire Reversal",
        "Claims & Misc Adjustment",
        "Brokerage (%)",
    ],
};
const SECTION_MAIN_LABELS = [
    "Base Rate",
    "Additional Rate",
    "Lumpsum Charges",
    "Demurrage",
    "Despatch",
    "Adjustments",
];

const columns = [
    "Label",
    "Cargo Name",
    "Cargo Quantity",
    "Unit",
    "Rate",
    "Amount",
    "CounterParty",
    "Pay/Receive",
    "Description",
    "HSN/SAC",
];

const PARTIAL_INPUT_COLUMNS = [
    "Label",
    "Cargo Name",
    "Rate",
    "Amount",
    "CounterParty",
    "Pay/Receive",
    "Description",
    "HSN/SAC",
];

export const CounterPartySelect = ({
    value = "",
    onChange,
    selectedPort,
    counterPartyDetails,
    onToggle,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [bankChoice, setBankChoice] = useState({});
    const dropdownRef = useRef(null);

    // Helpers
    const getShortName = (cp) => {
        if (!cp) return "N/A";
        return cp.type === "Individual"
            ? cp.individual_shortName ||
            `${cp.individual_firstName?.[0] || ""}${cp.individual_lastName?.[0] || ""
                }`.toUpperCase()
            : cp.organisation_shortName ||
            cp.organisation_name?.substring(0, 4).toUpperCase() ||
            "ORG";
    };

    const getFullName = (cp) =>
        !cp
            ? "Unknown"
            : cp.type === "Individual"
                ? `${cp.individual_title || ""} ${cp.individual_firstName || ""} ${cp.individual_middleName || ""
                    } ${cp.individual_lastName || ""}`.trim()
                : cp.organisation_fullName || "Unknown Organization";

    const getAddress = (cp) => {
        if (!cp) return "Address not available";
        if (cp.type === "Individual") {
            const parts = [
                cp.individual_homeAddress,
                cp.individual_personal_city,
                cp.individual_personal_state,
                cp.individual_personal_country,
            ].filter(Boolean);
            return parts.length
                ? parts.join(", ")
                : cp.individual_location || "Address not available";
        }
        const parts = [
            cp.organisation_officeAddress,
            cp.organisation_city,
            cp.organisation_state,
            cp.organisation_country,
        ].filter(Boolean);
        return parts.length ? parts.join(", ") : "Address not available";
    };

    const getRefNo = (cp) => cp?._id?.slice(-6).toUpperCase() || "N/A";

    const getSelectedBankAccountNumber = (cp, cpDetail) => {
        if (!cpDetail) return "N/A";

        const choiceType =
            bankChoice[cp.id] ||
            (cpDetail?.type === "Individual" ? "individual" : "organisation");

        if (choiceType === "individual") {
            return cpDetail.individual_bankAccountNumber || "N/A";
        } else {
            return cpDetail.organisation_bankAccountNumber || "N/A";
        }
    };

    const availableCounterParties = useMemo(() => {
        const withinPort = (selectedPort?.counterParties || []).map((cp) => ({
            ...cp,
            detail: (counterPartyDetails || []).find((d) => d._id === cp.id),
        }));
        if (!searchTerm) return withinPort;

        const q = searchTerm.toLowerCase();
        return withinPort.filter((cp) => {
            const d = cp.detail;
            const fullName = getFullName(d).toLowerCase();
            const shortName = getShortName(d).toLowerCase();
            const address = getAddress(d).toLowerCase();
            const accountNumber = getSelectedBankAccountNumber(cp, d).toLowerCase();
            return (
                fullName.includes(q) ||
                shortName.includes(q) ||
                address.includes(q) ||
                accountNumber.includes(q) ||
                cp.name?.toLowerCase().includes(q)
            );
        });
    }, [selectedPort, counterPartyDetails, searchTerm, bankChoice]);

    const CounterPartyIcon = ({ type }) =>
        type === "Individual" ? (
            <FiUser className="w-4 h-4 text-sky-600" />
        ) : (
            <MdOutlineBusiness className="w-4 h-4 text-emerald-600" />
        );

    const handleSelectCounterparty = (cp, cpDetail) => {
        if (typeof onChange === "function") {
            onChange({ target: { value: cp.id } });
        }
        setSearchTerm("");

        if (typeof onToggle === "function") {
            onToggle(false);
        }
    };

    return (
        <>
            <div
                ref={dropdownRef}
                className="z-[9999] bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden text-[11px]"
            >
                {/* Search */}
                <div className="p-1.5 border-b border-slate-200 bg-slate-50 sticky top-0">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by name, short name, bank account, address..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-8 pr-3 h-6 border border-slate-300 rounded-none text-[11px] focus:outline-none focus:ring-1 focus:ring-[#313C4C] font-medium"
                            autoFocus
                        />
                        <FiSearch className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    </div>
                </div>

                {/* Table Header */}
                <div className="bg-[#e2e8f0] border-b border-slate-200 px-3 py-0.5">
                    <div className="grid grid-cols-12 gap-3 text-[11px] font-medium text-[#1e293b] uppercase tracking-wider whitespace-nowrap">
                        <div className="col-span-1">Select</div>
                        <div className="col-span-1">Type</div>
                        <div className="col-span-2">Short Name</div>
                        <div className="col-span-2">Full Name</div>
                        <div className="col-span-2">Bank Account</div>
                        <div className="col-span-3">Address</div>
                        <div className="col-span-1">Ref</div>
                    </div>
                </div>

                {/* Results Table */}
                <div className="max-h-[420px] overflow-y-auto scroll-thin">
                    {availableCounterParties.length === 0 ? (
                        <div className="px-4 py-4 text-slate-400 text-[11px] italic text-center">
                            {searchTerm
                                ? "No counterparties found matching your search."
                                : "No counterparties available."}
                        </div>
                    ) : (
                        availableCounterParties.map((cp) => {
                            const d = cp.detail;
                            if (!d) return null;
                            const shortName = getShortName(d);
                            const fullName = getFullName(d);
                            const address = getAddress(d);
                            const refNo = getRefNo(d);
                            const accountNumber = getSelectedBankAccountNumber(cp, d);
                            if (!bankChoice[cp.id]) {
                                setBankChoice((s) => ({
                                    ...s,
                                    [cp.id]:
                                        d.type === "Individual" ? "individual" : "organisation",
                                }));
                            }

                            return (
                                <div key={cp.id} className="border-b border-slate-100">
                                    <div className={`px-3 py-0.5 hover:bg-slate-50 transition-colors ${cp.id === value ? 'bg-sky-50/60 font-medium' : ''
                                        }`}>
                                        <div className="grid grid-cols-12 gap-3 items-center text-[11px] cursor-pointer" onClick={() => handleSelectCounterparty(cp, d)}>
                                            <div className="col-span-1">
                                                <input
                                                    type="radio"
                                                    name="counterparty-selection"
                                                    className="w-3.5 h-3.5 text-[#313C4C] cursor-pointer"
                                                    checked={cp.id === value}
                                                    onChange={() => handleSelectCounterparty(cp, d)}
                                                />
                                            </div>

                                            <div className="col-span-1 flex items-center gap-1 text-slate-700">
                                                <span>{d.type.slice(0, 1)}</span>
                                                <CounterPartyIcon type={d.type} />
                                            </div>

                                            <div className="col-span-2 font-medium text-slate-800">
                                                {shortName}
                                            </div>

                                            <div className="col-span-2">
                                                <button
                                                    className="text-left w-full hover:text-[#313C4C] transition-colors"
                                                    onClick={() => handleSelectCounterparty(cp, d)}
                                                >
                                                    <div className="font-medium text-slate-800 truncate">
                                                        {fullName}
                                                    </div>
                                                </button>
                                            </div>

                                            <div className="col-span-2 font-mono text-slate-700">
                                                {accountNumber}
                                            </div>

                                            <div className="col-span-3">
                                                <div className="text-slate-600 truncate flex items-center gap-1">
                                                    <FiMapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                                    <span className="truncate">{address}</span>
                                                </div>
                                            </div>

                                            <div className="col-span-1">
                                                <span className="font-medium text-[#1e293b] text-[10px] px-1.5 py-[1px] rounded-sm bg-sky-50 border border-sky-300">
                                                    #{refNo}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </>
    );
};

const IssueInvoiceModel = ({
    opsId,
    cargoDetails,
    selectedPort,
    isReadOnly,
    initialRows = [],
    setInitialRows,
}) => {
    const [rows, setRows] = useState([]);
    const [liveInvoices, setLiveInvoices] = useState([]);
    const [counterPartyDetails, setCounterPartyDetails] = useState([]);
    const [counterPartyModalRow, setCounterPartyModalRow] = useState(null);
    const [activeTab, setActiveTab] = useState("Freight");

    const [clickedRowId, setClickedRowId] = useState(null);

    const fetchLiveInvoices = async () => {
        if (!opsId) return;
        try {
            const { data } = await getPostedInvoiceByUserId(opsId);
            if (Array.isArray(data)) {
                setLiveInvoices(data);
            }
        } catch (error) {
            console.error("Failed to fetch live invoices in IssueInvoiceModel:", error);
        }
    };

    useEffect(() => {
        fetchLiveInvoices();
    }, [opsId]);

    const handleIssueInvoice = (rowId) => {
        const newRow = rows.find((r) => r._id === rowId);
        if (!newRow) return;

        const newAmount = parseFloat(newRow.amount) || 0;
        const alreadyInvoicedAmount = getInvoicedAmount(newRow);

        if (alreadyInvoicedAmount > 0 && newAmount < alreadyInvoicedAmount) {
            toast.error(
                `Row amount (${newAmount}) cannot be less than the already invoiced amount (${alreadyInvoicedAmount}).`
            );
            return;
        }

        const existingInvoicedItem =
            (initialRows || []).find((r) => r._id === rowId) ||
            (selectedPort?.invoiceDetails || []).find((r) => r._id === rowId);

        setClickedRowId(rowId);

        setInitialRows((prevRows) => {
            const exists = prevRows.some((r) => r._id === rowId);

            if (exists) {
                return prevRows.map((r) => (r._id === rowId ? newRow : r));
            } else {
                return [...prevRows, newRow];
            }
        });

        toast.success(
            existingInvoicedItem ? "Invoice updated successfully!" : "Invoice issued successfully!"
        );

        setTimeout(() => {
            setClickedRowId(null);
        }, 2000);
    };

    const getInitialRate = (label) => {
        switch (label) {
            case "Base Rate":
                return selectedPort.freight || 0;
            case "Additional Rate":
            case "Lumpsum Charges":
                return selectedPort.lumpsumFreight || 0;
            case "Adcom (%)":
                return selectedPort.adcom || 0;
            case "Others (%)":
                return selectedPort.other || 0;
            case "Brokerage (%)":
                return selectedPort.brokerage || 0;
            case "Demurrage":
            case "Demurrage (Load Port)":
            case "Demurrage (Disch Port)":
            case "Demurrage Surcharge":
                return selectedPort.demurrage || 0;
            case "Despatch":
            case "Despatch (Load Port)":
            case "Despatch (Disch Port)":
            case "Despatch Adjustment":
                return selectedPort.despatch || 0;
            case "Adjustments":
            case "Fuel Settlement Adjustment":
            case "Off-Hire Hire Reversal":
            case "Claims & Misc Adjustment":
                return selectedPort.adjustments || 0;
            default:
                return 0;
        }
    };

    const isPercentageRow = (label) =>
        ["Adcom (%)", "Others (%)", "Brokerage (%)"].includes(label);

    useEffect(() => {
        if (!cargoDetails || !selectedPort) return;
        if (rows && rows.length > 0) return;

        const counterPartyObj = selectedPort.counterParties?.[0] || {};
        const counterPartyId = counterPartyObj.id || "";
        const counterParty = counterPartyObj.name || "";

        const mockRows = Object.entries(sectionDefinitions).flatMap(
            ([section, labels]) => {
                const mainRows = labels
                    .filter((label) => !isPercentageRow(label))
                    .map((label, idx) => {
                        let payReceive = "Payable";
                        if (section === "Freight")
                            payReceive = idx < 3 ? "Receivable" : "Payable";
                        else payReceive = idx === 0 ? "Receivable" : "Payable";

                        const rate = getInitialRate(label);
                        const qty = selectedPort.cargoQty || 0;
                        const amount = Number((qty * rate).toFixed(3));

                        return {
                            _id: uuidv4(),
                            section,
                            label,
                            key: label,
                            isDefault: true,
                            blDate: selectedPort.billOfLadingDate || "",
                            blQty: selectedPort.blNumber || 0,
                            remarks: selectedPort.remarks || "",
                            cargoName: cargoDetails.name || "",
                            cargoQty: qty,
                            unit: "MT",
                            rate,
                            amount,
                            payReceive,
                            counterPartyId,
                            counterParty,
                            description: `${label} for ${cargoDetails.name} for ${qty}MT@${rate}`,
                            hsn: "",
                        };
                    });

                const mainTotal = mainRows.reduce(
                    (sum, r) => sum + (parseFloat(r.amount) || 0),
                    0
                );

                const percentageRows = labels
                    .filter((label) => isPercentageRow(label))
                    .map((label) => {
                        const rate = getInitialRate(label);
                        const amount = Number(((rate * mainTotal) / 100).toFixed(3));

                        return {
                            _id: uuidv4(),
                            section,
                            label,
                            key: label,
                            isDefault: true,
                            cargoName: cargoDetails.name || "",
                            blDate: selectedPort.billOfLadingDate || "",
                            blQty: selectedPort.blNumber || 0,
                            remarks: selectedPort.remarks || "",
                            cargoQty: selectedPort.cargoQty || 0,
                            unit: "MT",
                            rate,
                            amount,
                            payReceive: "Payable",
                            counterPartyId,
                            counterParty,
                            description: `${label} @${rate} of ${mainTotal}`,
                            hsn: "",
                        };
                    });

                return [...mainRows, ...percentageRows];
            }
        );

        const merged = mockRows.map((mock) => {
            const existing = initialRows.find(
                (r) => r.label === mock.label && r.section === mock.section
            );
            return existing ? { ...mock, ...{ ...existing, blDate: selectedPort.billOfLadingDate || "", blQty: selectedPort.blNumber || 0, remarks: selectedPort.remarks || "" } } : mock;
        });

        const extras = initialRows.filter(
            (r) => !merged.some((m) => m.label === r.label && m.section === r.section)
        );

        setRows([...merged, ...extras]);
    }, [cargoDetails, selectedPort, initialRows]);


    const handleAddRow = (afterRowId, section) => {
        const afterRow = rows.find((r) => r._id === afterRowId);
        if (!afterRow) return;

        const isPercentage = isPercentageRow(afterRow.label);

        const counterPartyObj = selectedPort.counterParties?.[0] || {};
        const counterPartyId = counterPartyObj.id || "";
        const counterParty = counterPartyObj.name || "";

        const initialRate = getInitialRate(afterRow.label) || 0;
        const initialQty = selectedPort?.cargoQty ?? 0;
        const initialQtyNum = parseFloat(initialQty) || 0;

        const newRow = {
            _id: uuidv4(),
            section,
            label: afterRow.label,
            isDefault: false,
            key: afterRow.label,
            cargoName: cargoDetails?.name || "",
            cargoQty: initialQty,
            blDate: selectedPort?.billOfLadingDate || "",
            blQty: selectedPort?.blNumber || "",
            remarks: selectedPort?.remarks || "",
            unit: "MT",
            rate: initialRate,
            amount: initialQtyNum * initialRate,
            counterPartyId,
            counterParty,
            payReceive: "Payable",
            description: `${afterRow.label} for ${cargoDetails?.name || ""} for ${initialQtyNum}MT@${initialRate}`,
            hsn: "",
            subsection: isPercentage ? "percentage" : "main",
        };

        setRows((prev) => {
            const afterIndex = prev.findIndex((r) => r._id === afterRowId);
            const updated = [...prev];
            updated.splice(afterIndex + 1, 0, newRow);
            return updated;
        });
    };

    const handleDeleteRow = (rowId) => {
        setRows((prev) => prev.filter((row) => row._id !== rowId));
    };

    const handleChange = (rowId, field, value) => {
        setRows((prev) => {
            if (!Array.isArray(prev)) return [];

            const updatedRows = prev.map((row) => {
                if (row._id !== rowId) return row;

                let updated = { ...row };

                if (field === "counterPartyId") {
                    const counterParty = counterPartyDetails?.find((cp) => cp._id === value);
                    updated.counterPartyId = counterParty?._id || "";
                    updated.counterParty =
                        counterParty?.type === "Individual"
                            ? `${counterParty.individual_title || ""} ${counterParty.individual_firstName || ""} ${counterParty.individual_middleName || ""} ${counterParty.individual_lastName || ""}`.trim()
                            : counterParty?.organisation_name ||
                            counterParty?.organisation_fullName ||
                            `${counterParty?.organisation_firstName} ${counterParty?.organisation_lastName}` ||
                            counterParty?.fullName ||
                            "";

                    const counterPartyDetail = counterPartyDetails.find((cp) => cp._id === value);
                    if (counterPartyDetail) {
                        updated.counterPartyDetail = counterPartyDetail;
                    }
                }
                else if (field === "label") {
                    updated.label = value;
                    const initialRate = getInitialRate(value) || 0;
                    updated.rate = initialRate;
                    const defaultQty = selectedPort?.cargoQty ?? 0;
                    updated.cargoQty = defaultQty;
                    const qty = parseFloat(defaultQty) || 0;
                    updated.amount = Number((qty * initialRate).toFixed(3));
                    updated.description = `${value} for ${updated.cargoName} for ${qty}${updated.unit || "MT"}@${initialRate}`;
                }
                else {
                    updated[field] = value;
                }

                if (!isPercentageRow(updated.label)) {
                    const qty = parseFloat(updated.cargoQty) || 0;
                    const rate = parseFloat(updated.rate) || 0;
                    updated.amount = Number((qty * rate).toFixed(3));
                    updated.description = `${updated.label} for ${updated.cargoName} for ${qty}${updated.unit || "MT"}@${rate}`;
                }

                return updated;
            });

            return updatedRows.map((row) => {
                if (isPercentageRow(row.label)) {
                    const mainTotal = updatedRows
                        .filter((r) => r.section === row.section && !isPercentageRow(r.label))
                        .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

                    const rate = parseFloat(row.rate) || 0;
                    const amount = Number(((rate * mainTotal) / 100).toFixed(3));

                    return {
                        ...row,
                        amount,
                        description: `${row.label} @${rate} of ${mainTotal}`,
                    };
                }
                return row;
            });
        });
    };

    const fetchCounterPartyDetails = async () => {
        const userId = getCurrentUserId();
        try {
            const userRes = await getAddressDetails({
                userId,
                addressBookType: "user",
                perPage: 100,
            });

            setCounterPartyDetails(userRes?.data?.list);
        } catch (error) {
            console.error("Failed to fetch counter party details:", error);
            setCounterPartyDetails([]);
        }
    };

    useEffect(() => {
        fetchCounterPartyDetails();
    }, []);

    const sectionTotals = useMemo(() => {
        const totals = {};
        Object.keys(sectionDefinitions).forEach((section) => {
            const mainTotal = rows
                .filter((r) => r.section === section && !isPercentageRow(r.label))
                .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
            totals[section] = mainTotal;
        });
        return totals;
    }, [rows]);

    const getInvoicedAmount = (row) => {
        if (!row) return 0;

        let settled = 0;
        let matched = false;

        const currentPort = (selectedPort?.port || selectedPort?.name || "").trim().toLowerCase();
        const currentLabel = (row.label || "").trim().toLowerCase();
        const rowId = row._id ? row._id.toString() : "";

        // 1. Search in live invoices from Accounting / Backend
        if (Array.isArray(liveInvoices) && liveInvoices.length > 0) {
            for (const inv of liveInvoices) {
                const details = inv.invoiceDetail || [];
                for (const item of details) {
                    const detail = item.invoiceDetail || item;
                    const targetId = (detail._id || item._id || "").toString();
                    const itemPort = (item.port || detail.port || "").trim().toLowerCase();
                    const itemLabel = (detail.label || item.label || "").trim().toLowerCase();

                    const getSettled = (d) => {
                        if (d?.totalSettledAmt !== undefined && d?.totalSettledAmt !== null && !isNaN(d.totalSettledAmt)) {
                            return Number(d.totalSettledAmt);
                        }
                        if (Array.isArray(d?.paymentEntries)) {
                            return d.paymentEntries.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                        }
                        return 0;
                    };

                    const amt = getSettled(item) || getSettled(detail);

                    // Check exact ID match
                    if (rowId && targetId && rowId === targetId) {
                        settled += amt;
                        matched = true;
                    }
                    // Or match by port & label
                    else if (!matched && itemLabel && itemLabel === currentLabel && (!currentPort || !itemPort || itemPort === currentPort)) {
                        settled += amt;
                    }
                }
            }
        }

        // 2. Also check if row itself or existing item in initialRows has totalSettledAmt / paymentEntries
        const getDirectSettled = (d) => {
            if (d?.totalSettledAmt !== undefined && d?.totalSettledAmt !== null && !isNaN(d.totalSettledAmt)) {
                return Number(d.totalSettledAmt);
            }
            if (Array.isArray(d?.paymentEntries)) {
                return d.paymentEntries.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
            }
            return 0;
        };

        const directRowSettled = getDirectSettled(row);
        if (directRowSettled > settled) {
            settled = directRowSettled;
        }

        const existingInvoicedItem =
            (initialRows || []).find((r) => r._id === row._id || (r.label === row.label && r.section === row.section)) ||
            (selectedPort?.invoiceDetails || []).find((r) => r._id === row._id || (r.label === row.label && r.section === row.section));

        const existingSettled = getDirectSettled(existingInvoicedItem);
        if (existingSettled > settled) {
            settled = existingSettled;
        }

        return Number(settled.toFixed(3));
    };

    const getRemainingAmount = (row, invoicedAmt) => {
        const mainAmt = parseFloat(row?.amount) || 0;
        return Number(Math.max(0, mainAmt - invoicedAmt).toFixed(3));
    };

    const renderInputCell = (row, column, isFullInput = false, isLast = false) => {
        const commonClasses =
            "w-full h-full text-[11px] px-1.5 py-0 border-none outline-none focus:outline-none focus:ring-1 focus:ring-inset focus:ring-amber-500 text-slate-800 bg-[#ffffd6] focus:bg-white font-medium capitalize";
        const disabledClasses = "bg-[#f0f4f9] text-slate-700 cursor-not-allowed font-medium";
        const borderBClass = isLast ? "" : "border-b";

        switch (column) {
            case "Label":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-32 h-[20px] align-middle ${row.isDefault ? "bg-white" : "bg-[#ffffd6]"}`} key={column}>
                        {row.isDefault ? (
                            <div className="px-1.5 py-0 text-[11px] font-semibold text-slate-900 capitalize truncate h-full flex items-center">{row.label}</div>
                        ) : (
                            <select
                                className={`${commonClasses} cursor-pointer`}
                                value={row.label}
                                onChange={(e) => handleChange(row._id, "label", e.target.value)}
                            >
                                {(row.subsection === "main"
                                    ? SECTION_MAIN_LABELS || []
                                    : ["Adcom (%)", "Others (%)", "Brokerage (%)"]
                                ).map((lbl) => (
                                    <option key={lbl} value={lbl}>
                                        {lbl}
                                    </option>
                                ))}
                            </select>
                        )}
                    </td>
                );

            case "Cargo Name":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-36 min-w-[130px] h-[20px] align-middle bg-[#f0f4f9]`} key={column}>
                        <input
                            type="text"
                            className={`${commonClasses} ${disabledClasses} text-center`}
                            disabled
                            readOnly
                            value={row.cargoName || ""}
                            title={row.cargoName || ""}
                        />
                    </td>
                );

            case "Cargo Quantity":
                return isFullInput ? (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-20 h-[20px] align-middle bg-[#ffffd6]`} key={column}>
                        <input
                            type="number"
                            disabled={isReadOnly}
                            readOnly={isReadOnly}
                            className={`${commonClasses} text-center ${isReadOnly ? disabledClasses : ""}`}
                            value={row.cargoQty ?? ""}
                            onChange={(e) =>
                                handleChange(row._id, "cargoQty", e.target.value)
                            }
                        />
                    </td>
                ) : (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] h-[20px] align-middle bg-white`} key={column}></td>
                );

            case "Unit":
                return isFullInput ? (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-16 h-[20px] align-middle bg-[#ffffd6]`} key={column}>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            readOnly={isReadOnly}
                            className={`${commonClasses} text-center ${isReadOnly ? disabledClasses : ""}`}
                            value={row.unit || ""}
                            onChange={(e) => handleChange(row._id, "unit", e.target.value)}
                        />
                    </td>
                ) : (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] h-[20px] align-middle bg-white`} key={column}></td>
                );

            case "Rate":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-20 h-[20px] align-middle bg-[#ffffd6]`} key={column}>
                        <input
                            type="number"
                            disabled={isReadOnly}
                            readOnly={isReadOnly}
                            className={`${commonClasses} text-center ${isReadOnly ? disabledClasses : ""}`}
                            value={row.rate ?? ""}
                            onChange={(e) => handleChange(row._id, "rate", e.target.value)}
                        />
                    </td>
                );

            case "Amount": {
                const existingInvoicedItem =
                    (initialRows || []).find((r) => r._id === row._id) ||
                    (selectedPort?.invoiceDetails || []).find((r) => r._id === row._id);
                const alreadyInvoicedAmt = existingInvoicedItem
                    ? (parseFloat(existingInvoicedItem.amount) ?? parseFloat(existingInvoicedItem.invoicedAmount) ?? 0)
                    : (parseFloat(row.invoicedAmount) || 0);
                const isInvalidAmount = alreadyInvoicedAmt > 0 && (parseFloat(row.amount) || 0) < alreadyInvoicedAmt;

                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-24 h-[20px] align-middle ${isInvalidAmount ? "bg-rose-100" : "bg-[#f0f4f9]"}`} key={column} title={isInvalidAmount ? `Amount cannot be less than invoiced amount (${alreadyInvoicedAmt})` : ""}>
                        <input
                            type="number"
                            className={`${commonClasses} ${isInvalidAmount ? "bg-rose-100 text-rose-800 font-semibold" : disabledClasses} text-center font-medium text-slate-800`}
                            disabled
                            readOnly
                            value={row.amount ?? ""}
                        />
                    </td>
                );
            }

            case "CounterParty":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-36 min-w-[140px] h-[20px] align-middle bg-[#ffffd6]`} key={column}>
                        <button
                            type="button"
                            disabled={isReadOnly}
                            className={`w-full h-full px-1.5 py-0 text-left flex items-center justify-between cursor-pointer border-none outline-none font-medium text-[11px] focus:bg-white focus:ring-1 focus:ring-inset focus:ring-amber-500 ${isReadOnly ? disabledClasses : "bg-[#ffffd6]"}`}
                            onClick={() => setCounterPartyModalRow(row._id)}
                            title={row.counterParty || "Select Counterparty..."}
                        >
                            <span
                                className={`block truncate ${row.counterPartyId ? "text-slate-800 font-medium" : "text-slate-400"
                                    } text-[11px] capitalize`}
                            >
                                {row.counterPartyId
                                    ? row.counterParty
                                    : "Select Counterparty..."}
                            </span>
                            <FiChevronDown className="w-3 h-3 text-slate-500 flex-shrink-0 ml-1" />
                        </button>
                    </td>
                );

            case "Pay/Receive":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-32 min-w-[120px] h-[20px] align-middle bg-[#ffffd6]`} key={column}>
                        <select
                            className={`${commonClasses} cursor-pointer`}
                            value={row.payReceive || "Payable"}
                            onChange={(e) =>
                                handleChange(row._id, "payReceive", e.target.value)
                            }
                        >
                            <option value="Payable">Payable</option>
                            <option value="Receivable">Receivable</option>
                        </select>
                    </td>
                );

            case "Description":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] min-w-[340px] h-[20px] align-middle bg-[#f0f4f9]`} key={column}>
                        <input
                            type="text"
                            className={`${commonClasses} min-w-[340px] ${disabledClasses}`}
                            disabled
                            readOnly
                            value={row.description || ""}
                            title={row.description || ""}
                        />
                    </td>
                );

            case "HSN/SAC":
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-20 h-[20px] align-middle bg-[#ffffd6]`} key={column}>
                        <input
                            type="text"
                            disabled={isReadOnly}
                            readOnly={isReadOnly}
                            className={`${commonClasses} text-center ${isReadOnly ? disabledClasses : ""}`}
                            value={row.hsn || ""}
                            onChange={(e) => handleChange(row._id, "hsn", e.target.value)}
                        />
                    </td>
                );

            default:
                return (
                    <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] h-[20px] align-middle bg-white`} key={column}></td>
                );
        }
    };

    const renderActionsCellForAdd = (row, isLast = false) => (
        <td className={`p-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] w-6 text-center h-[20px] align-middle bg-white`}>
            <div className="flex items-center justify-center h-full w-full">
                <button
                    className="w-full h-[19px] p-0 inline-flex items-center justify-center text-slate-800 hover:text-slate-900 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                    onClick={() => handleAddRow(row._id, row.section)}
                    disabled={isReadOnly}
                    title="Add row"
                    type="button"
                >
                    <FiPlus size={11} />
                </button>
            </div>
        </td>
    );

    const renderActionsCellForDelete = (row, isLast = false) => {
        const isCreated = (selectedPort?.invoiceDetails || []).some(
            (item) => item._id === row._id
        ) || (initialRows || []).some(
            (item) => item._id === row._id
        );

        const existingInvoicedItem =
            (initialRows || []).find((r) => r._id === row._id) ||
            (selectedPort?.invoiceDetails || []).find((r) => r._id === row._id);
        const alreadyInvoicedAmt = existingInvoicedItem
            ? (parseFloat(existingInvoicedItem.amount) ?? parseFloat(existingInvoicedItem.invoicedAmount) ?? 0)
            : (parseFloat(row.invoicedAmount) || 0);
        const isInvalidAmount = alreadyInvoicedAmt > 0 && (parseFloat(row.amount) || 0) < alreadyInvoicedAmt;

        return (
            <td className={`p-0 ${isLast ? "" : "border-b"} border-[#cbd5e1] w-[68px] text-center h-[20px] align-middle bg-white`}>
                <div className="flex items-center justify-center gap-1 h-full w-full px-0.5">
                    <button
                        className={`w-full flex-1 px-1 py-[1.5px] h-[17px] inline-flex items-center justify-center rounded-none text-[9.5px] leading-none font-medium transition cursor-pointer capitalize ${clickedRowId === row._id
                            ? "bg-emerald-600 text-white"
                            : isCreated
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-[#313C4C] text-white hover:bg-[#1e2632]"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        onClick={() => handleIssueInvoice(row._id)}
                        title={isInvalidAmount ? `Cannot update: amount is less than invoiced amount (${alreadyInvoicedAmt})` : isCreated ? "Update Invoice" : "Issue Invoice"}
                        disabled={clickedRowId === row._id || isReadOnly}
                        type="button"
                    >
                        {clickedRowId === row._id ? (
                            <FiCheck size={9.5} />
                        ) : isCreated ? (
                            "Update"
                        ) : (
                            "Create"
                        )}
                    </button>
                    {!row.isDefault && (
                        <button
                            className="w-[18px] h-[17px] p-0 inline-flex items-center justify-center rounded-none bg-rose-50 text-rose-700 hover:bg-rose-100 transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
                            onClick={() => handleDeleteRow(row._id)}
                            disabled={isReadOnly}
                            title="Delete row"
                            type="button"
                        >
                            <FiTrash2 size={9.5} />
                        </button>
                    )}
                </div>
            </td>
        );
    };

    const renderDataRow = (row, isFullInput = false, isLast = false) => (
        <tr
            key={row._id}
            className="h-[20px] bg-white hover:bg-slate-50/60 transition-colors"
        >
            {renderActionsCellForAdd(row, isLast)}
            {columns.map((col) => {
                if (isFullInput || PARTIAL_INPUT_COLUMNS.includes(col)) {
                    return renderInputCell(row, col, isFullInput, isLast);
                }
                return (
                    <td className={`p-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] h-[20px] align-middle bg-white`} key={col}></td>
                );
            })}
            {renderActionsCellForDelete(row, isLast)}
        </tr>
    );

    const renderInvoicedRow = (row, isFullInput = false, isLast = false) => {
        const invoicedAmt = getInvoicedAmount(row);
        const borderBClass = isLast ? "" : "border-b";
        return (
            <tr
                key={`${row._id}-invoiced`}
                className="h-[20px] bg-white hover:bg-slate-50/50 text-slate-800 font-medium transition-colors"
            >
                <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-6 bg-white`}></td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] w-32 h-[20px] align-middle bg-white text-[11px] font-medium text-slate-800`}>
                    <div className="flex items-center gap-1.5 pl-1">
                        <span className="text-sky-700 font-bold text-[10px]">↳</span>
                        <span className="text-sky-900 font-medium text-[11px]">Invoiced</span>
                    </div>
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle w-36 min-w-[130px] whitespace-nowrap`} title={row.cargoName || ""}>
                    {row.cargoName || ""}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {isFullInput ? row.cargoQty || 0 : ""}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {isFullInput ? row.unit || "MT" : ""}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {row.rate ?? 0}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center font-semibold text-slate-900 bg-white h-[20px] align-middle`}>
                    {invoicedAmt}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-left text-slate-700 truncate w-36 min-w-[140px] bg-white h-[20px] align-middle`} title={row.counterParty || "-"}>
                    {row.counterParty || "-"}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-left text-slate-700 bg-white h-[20px] align-middle`}>
                    {row.payReceive || "Payable"}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-left text-slate-600 min-w-[340px] bg-white h-[20px] align-middle whitespace-nowrap`} title="Invoiced Amount">
                    Invoiced Amount
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {row.hsn || ""}
                </td>
                <td className={`p-0 ${borderBClass} border-[#cbd5e1] w-[68px] bg-white`}></td>
            </tr>
        );
    };

    const renderRemainingRow = (row, isFullInput = false, isLast = false) => {
        const invoicedAmt = getInvoicedAmount(row);
        const remainingAmt = getRemainingAmount(row, invoicedAmt);
        const borderBClass = isLast ? "" : "border-b";
        return (
            <tr
                key={`${row._id}-remaining`}
                className="h-[20px] bg-white hover:bg-slate-50/50 text-slate-800 font-medium transition-colors"
            >
                <td className={`p-0 border-r ${borderBClass} border-[#cbd5e1] w-6 bg-white`}></td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] w-32 h-[20px] align-middle bg-white text-[11px] font-medium text-slate-800`}>
                    <div className="flex items-center gap-1.5 pl-1">
                        <span className="text-amber-700 font-bold text-[10px]">↳</span>
                        <span className="text-amber-900 font-medium text-[11px]">Remaining</span>
                    </div>
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle w-36 min-w-[130px] whitespace-nowrap`} title={row.cargoName || ""}>
                    {row.cargoName || ""}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {isFullInput ? row.cargoQty || 0 : ""}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {isFullInput ? row.unit || "MT" : ""}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {row.rate ?? 0}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center font-semibold text-slate-900 bg-white h-[20px] align-middle`}>
                    {remainingAmt}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-left text-slate-700 truncate w-36 min-w-[140px] bg-white h-[20px] align-middle`} title={row.counterParty || "-"}>
                    {row.counterParty || "-"}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-left text-slate-700 bg-white h-[20px] align-middle`}>
                    {row.payReceive || "Payable"}
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-left text-slate-600 min-w-[340px] bg-white h-[20px] align-middle whitespace-nowrap`} title="Remaining Balance">
                    Remaining Balance
                </td>
                <td className={`px-1.5 py-0 border-r ${borderBClass} border-[#cbd5e1] text-center text-slate-700 bg-white h-[20px] align-middle`}>
                    {row.hsn || ""}
                </td>
                <td className={`p-0 ${borderBClass} border-[#cbd5e1] w-[68px] bg-white`}></td>
            </tr>
        );
    };

    const renderTotalRow = (section, keyPrefix = "", isLast = false) => (
        <tr
            key={`${keyPrefix}${section}-total`}
            className="bg-[#f0f4f9] font-semibold text-slate-900 h-[20px]"
        >
            <td className={`p-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] h-[20px] align-middle bg-[#f0f4f9]`}></td>
            <td className={`px-1.5 py-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] text-left font-bold text-slate-900 text-[11px] align-middle h-[20px] capitalize bg-[#f0f4f9]`}>
                Total
            </td>
            {columns.slice(1, 5).map((col) => (
                <td className={`p-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] h-[20px] align-middle bg-[#f0f4f9]`} key={col}></td>
            ))}
            <td className={`px-1.5 py-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] text-center text-slate-950 font-bold text-[11px] align-middle h-[20px] bg-[#f0f4f9]`}>
                {sectionTotals[section] || 0}
            </td>
            {columns.slice(6).map((col) => (
                <td className={`p-0 border-r ${isLast ? "" : "border-b"} border-[#cbd5e1] h-[20px] align-middle bg-[#f0f4f9]`} key={col}></td>
            ))}
            <td className={`p-0 ${isLast ? "" : "border-b"} border-[#cbd5e1] w-[68px] h-[20px] align-middle bg-[#f0f4f9]`}></td>
        </tr>
    );

    const renderSectionRows = (section) => {
        const sectionRows = rows.filter((r) => r.section === section);

        const mainSubsection = sectionRows.filter((r) => !isPercentageRow(r.label));
        const percentageSubsection = sectionRows.filter((r) =>
            isPercentageRow(r.label)
        );

        const allRowItems = [];

        mainSubsection.forEach((row) => {
            allRowItems.push({ type: "main", row, isFullInput: true });
            allRowItems.push({ type: "invoiced", row, isFullInput: true });
            allRowItems.push({ type: "remaining", row, isFullInput: true });
        });

        allRowItems.push({ type: "total" });

        percentageSubsection.forEach((row) => {
            allRowItems.push({ type: "main", row, isFullInput: false });
            allRowItems.push({ type: "invoiced", row, isFullInput: false });
            allRowItems.push({ type: "remaining", row, isFullInput: false });
        });

        return allRowItems.map((item, index) => {
            const isLast = index === allRowItems.length - 1;
            if (item.type === "total") {
                return renderTotalRow(section, "", isLast);
            }
            if (item.type === "invoiced") {
                return renderInvoicedRow(item.row, item.isFullInput, isLast);
            }
            if (item.type === "remaining") {
                return renderRemainingRow(item.row, item.isFullInput, isLast);
            }
            return renderDataRow(item.row, item.isFullInput, isLast);
        });
    };

    const handleCounterPartySelect = (e, contactType, bankInfo) => {
        if (counterPartyModalRow) {
            handleChange(
                counterPartyModalRow,
                "counterPartyId",
                e.target.value,
            );
            setCounterPartyModalRow(null);
        }
    };

    const currentModalRow =
        rows.find((r) => r._id === counterPartyModalRow) || {};

    const tabsList = Object.keys(sectionDefinitions);
    const visibleSections = [activeTab];

    return (
        <div className="text-[11px] font-medium">
            {/* Port Information Banner */}
            {selectedPort && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-1 bg-[#f0f4f9] border border-[#cbd5e1] text-[11px] text-slate-800 font-medium mb-2">
                    <div className="flex items-center gap-3">
                        <span>
                            Port: <strong className="font-medium text-[#0f4c81]">{selectedPort.port || "Port"}</strong>
                        </span>
                        <span className="text-slate-300">|</span>
                        <span>
                            Operation: <strong className="font-medium text-slate-800 capitalize">{selectedPort.operation || "Port Stay"}</strong>
                        </span>
                        <span className="text-slate-300">|</span>
                        <span>
                            Cargo Qty: <strong className="font-medium text-slate-800">{selectedPort.cargoQty || 0} MT</strong>
                        </span>
                        <span className="text-slate-300">|</span>
                        <span>
                            B/L Date: <strong className="font-medium text-slate-800">{selectedPort.billOfLadingDate || selectedPort.arrivalTime || "-"}</strong>
                        </span>
                    </div>
                </div>
            )}

            {/* Tabs Navigation for Issue Freight / Invoices */}
            <div className="flex items-center gap-1 border-b border-[#cbd5e1] relative z-10 pt-1 -mb-[1px]">
                {tabsList.map((tab) => {
                    const count = rows.filter((r) => r.section === tab).length;
                    const isActive = activeTab === tab;

                    return (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => setActiveTab(tab)}
                            className={`px-3 py-1 text-[11px] h-[26px] flex items-center gap-1.5 cursor-pointer rounded-t-sm transition-all ${isActive
                                    ? "bg-white border-t-2 border-t-[#0f4c81] border-x border-b-white border-[#cbd5e1] text-[#0f4c81] font-semibold relative z-10 -mb-[1px]"
                                    : "bg-[#f1f5f9] border border-[#cbd5e1] text-slate-600 font-medium hover:bg-slate-200/60"
                                }`}
                        >
                            <span>{tab}</span>
                            {count > 0 && (
                                <span
                                    className={`px-1.5 py-0.2 rounded-full text-[10px] ${isActive
                                            ? "bg-amber-100 text-amber-900 font-semibold border border-amber-300"
                                            : "bg-slate-200 text-slate-600 font-medium"
                                        }`}
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {visibleSections.map((section) => (
                <div
                    key={section}
                    className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden relative z-0"
                >
                    <div className="overflow-x-auto scroll-thin">
                        <table className="w-full text-[11px] font-medium text-left border-collapse">
                            <thead>
                                <tr className="bg-[#e2e8f0] text-[#1e293b] h-[20px]">
                                    <th className="p-0 text-center font-medium text-[11px] text-[#1e293b] w-6 whitespace-nowrap border-r border-b border-[#cbd5e1]"></th>
                                    {columns.map((col) => (
                                        <th
                                            key={col}
                                            className={`px-1.5 py-0.5 font-medium text-[11px] text-[#1e293b] text-left whitespace-nowrap border-r border-b border-[#cbd5e1] capitalize ${
                                                col === "Cargo Name" ? "w-36 min-w-[130px]" :
                                                col === "Description" ? "min-w-[340px]" :
                                                col === "CounterParty" ? "w-36 min-w-[140px]" : ""
                                            }`}
                                        >
                                            {col}
                                        </th>
                                    ))}
                                    <th className="px-1.5 py-0.5 text-center font-medium text-[11px] text-[#1e293b] w-[68px] whitespace-nowrap border-b border-[#cbd5e1] capitalize">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">{renderSectionRows(section)}</tbody>
                        </table>
                    </div>
                </div>
            ))}

            {/* CounterParty Selection Modal */}
            <CustomShowModal
                open={counterPartyModalRow !== null}
                title="Select Counterparty"
                maxWidth="md"
                contentClassName="!p-1.5"
                saveLabel=""
                handleClose={() => setCounterPartyModalRow(null)}
            >
                <CounterPartySelect
                    value={currentModalRow.counterPartyId || ""}
                    onChange={handleCounterPartySelect}
                    selectedPort={selectedPort}
                    counterPartyDetails={counterPartyDetails}
                    placeholder="Select a counterparty..."
                    isOpen={true}
                    onToggle={() => { }}
                />
            </CustomShowModal>
        </div>
    );
};

export default IssueInvoiceModel;
